import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  BookOpenCheck,
  CheckCircle2,
  CircleDashed,
  Droplets,
  ExternalLink,
  Gauge,
  ListChecks,
  Route,
  Snowflake,
  Sparkles,
  Zap,
  type LucideIcon,
} from 'lucide-react';

import {
  buildComparableProjectsPresentation,
  COMPARABLE_PROJECT_FACETS,
  type ComparableProjectDatum,
  type ComparableProjectFacet,
  type ComparableProjectPeerPresentation,
} from '../../../lib/findings/comparableProjectsPresentation';
import { cn } from '../../../lib/utils';
import type { EconomicEvidenceBasis } from '../../../types/economicContribution';
import type { EconomicResearchResultSnapshot } from '../../../types/economicResearch';
import {
  EconomicEvidenceResearchStatus,
  EconomicEvidenceTrailSnapshotList,
} from './EconomicEvidenceResearchStatus';
import type { EconomicEvidenceResearchController } from './useEconomicEvidenceResearch';

const FACET_PRESENTATION: Record<ComparableProjectFacet, { label: string; icon: LucideIcon }> = {
  overview: { label: 'Overview', icon: ListChecks },
  water: { label: 'Water', icon: Droplets },
  cooling: { label: 'Cooling', icon: Snowflake },
  power: { label: 'Power', icon: Zap },
  delivery: { label: 'Delivery', icon: Route },
};

const BASIS_LABEL: Record<Exclude<EconomicEvidenceBasis, 'modelled' | 'operator-provided'>, string> = {
  'verified-actual': 'Verified actual',
  'observed-public-record': 'Observed record',
  committed: 'Committed',
  announced: 'Announced',
  'secondary-discovery': 'Discovery only',
};

type TabNavigationKey = 'ArrowDown' | 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'End' | 'Home';

function nextTabIndex(
  key: string,
  currentIndex: number,
  tabCount: number,
  orientation: 'horizontal' | 'vertical',
): number | null {
  if (tabCount < 1) return null;
  const navigationKey = key as TabNavigationKey;
  if (navigationKey === 'Home') return 0;
  if (navigationKey === 'End') return tabCount - 1;
  if (
    (orientation === 'horizontal' && navigationKey === 'ArrowRight')
    || (orientation === 'vertical' && navigationKey === 'ArrowDown')
  ) {
    return (currentIndex + 1) % tabCount;
  }
  if (
    (orientation === 'horizontal' && navigationKey === 'ArrowLeft')
    || (orientation === 'vertical' && navigationKey === 'ArrowUp')
  ) {
    return (currentIndex - 1 + tabCount) % tabCount;
  }
  return null;
}

function focusTab(tabId: string): void {
  document.getElementById(tabId)?.focus();
}

/**
 * Every value rendered through this helper is a calendar-only observation date
 * (`datum.asOf`, and `peer.updatedAt`, which is the latest of `asOf` /
 * `periodEnd` / `occurredOn`). Date-only ISO strings parse as UTC midnight, so
 * they must be formatted in UTC — otherwise every viewer west of Greenwich
 * reads the previous day. No real instant reaches this function.
 */
function formatDate(value: string): string {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp)
    ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(timestamp))
    : value;
}

function conciseFact(peer: ComparableProjectPeerPresentation, label: string): string | null {
  return peer.facts.overview.find((datum) => datum.label === label)?.value ?? null;
}

function strongestWaterFact(peer: ComparableProjectPeerPresentation): {
  stage: string;
  values: readonly string[];
} | null {
  const strongestStage = [...peer.waterTimeline]
    .reverse()
    .find((stage) => stage.observations.some((observation) => observation.kind === 'fact'));
  const observations = strongestStage?.observations.filter((candidate) => candidate.kind === 'fact') ?? [];
  return strongestStage && observations.length > 0
    ? { stage: strongestStage.label, values: observations.map((observation) => observation.value) }
    : null;
}

function coolingFact(peer: ComparableProjectPeerPresentation): string | null {
  return peer.facts.cooling.find((datum) => datum.kind === 'fact')?.value ?? null;
}

function regionalRelevance(peer: ComparableProjectPeerPresentation): string {
  const hasSameStateContext = peer.matchReasons.some((reason) => /same[- ]state context/iu.test(reason));
  return hasSameStateContext
    ? 'Same-state context; equivalent climate and water availability are not assumed.'
    : 'Location shown for context; climate equivalence is not established by the reviewed record.';
}

const WaterCoolingComparison: React.FC<{
  peers: readonly ComparableProjectPeerPresentation[];
  hasCompatibleCoolingPattern: boolean;
}> = ({ peers, hasCompatibleCoolingPattern }) => {
  const waterCount = peers.filter((peer) => strongestWaterFact(peer) != null).length;
  const coolingCount = peers.filter((peer) => coolingFact(peer) != null).length;
  const reportedActualCount = peers.filter((peer) => peer.waterTimeline.some((stage) => (
    stage.stage === 'reported-actual'
    && stage.observations.some((observation) => observation.kind === 'fact')
  ))).length;
  const sameStateCount = peers.filter((peer) => (
    peer.matchReasons.some((reason) => /same[- ]state context/iu.test(reason))
  )).length;
  return (
    <section className="mt-4 border-y border-border-subtle py-3" data-testid="comparable-water-cooling-comparison">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h4 className="text-xs font-semibold text-text-primary">Water and cooling comparison</h4>
          <p className="mt-1 text-[11px] leading-relaxed text-text-muted">
            Strongest reviewed water stage and documented cooling configuration for each shortlisted peer.
          </p>
        </div>
        <p className="font-mono type-meta tracking-[0.08em] text-text-muted">
          Water {waterCount}/{peers.length} · Cooling {coolingCount}/{peers.length}
        </p>
      </div>

      <dl className="mt-3 grid gap-x-5 gap-y-2 border-y border-border-subtle py-2.5 sm:grid-cols-3" data-testid="comparable-operational-readout">
        <div>
          <dt className="font-mono type-meta font-semibold uppercase tracking-[0.08em] text-text-muted">Regional lens</dt>
          <dd className="mt-0.5 text-[11px] leading-relaxed text-text-primary">
            {sameStateCount > 0
              ? `${sameStateCount} same-state ${sameStateCount === 1 ? 'record is' : 'records are'} prioritized; climate equivalence is not assumed.`
              : 'No same-state record is available in this shortlist.'}
          </dd>
        </div>
        <div>
          <dt className="font-mono type-meta font-semibold uppercase tracking-[0.08em] text-text-muted">Operating water</dt>
          <dd className="mt-0.5 text-[11px] leading-relaxed text-text-primary">
            {reportedActualCount > 0
              ? `${reportedActualCount} ${reportedActualCount === 1 ? 'project has' : 'projects have'} reported actual use; service limits stay separate.`
              : 'No operating actual is available; displayed quantities are pre-operation records.'}
          </dd>
        </div>
        <div>
          <dt className="font-mono type-meta font-semibold uppercase tracking-[0.08em] text-text-muted">Common approach</dt>
          <dd className="mt-0.5 text-[11px] leading-relaxed text-text-primary">
            {hasCompatibleCoolingPattern
              ? 'A cooling pattern supported by at least three compatible projects is shown below.'
              : 'No single regional cooling approach reaches the three-compatible-project threshold.'}
          </dd>
        </div>
      </dl>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[46rem] border-collapse text-left" aria-label="Comparable project water and cooling comparison">
          <thead>
            <tr className="border-b border-border-subtle font-mono type-meta font-semibold uppercase tracking-[0.08em] text-text-muted">
              <th scope="col" className="w-[18%] px-2 py-2">Project</th>
              <th scope="col" className="w-[29%] px-2 py-2">Water record</th>
              <th scope="col" className="w-[29%] px-2 py-2">Cooling infrastructure</th>
              <th scope="col" className="w-[24%] px-2 py-2">Regional relevance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {peers.map((peer) => {
              const water = strongestWaterFact(peer);
              const cooling = coolingFact(peer);
              return (
                <tr key={peer.projectId} className="align-top text-[10px] leading-relaxed text-text-secondary">
                  <th scope="row" className="px-2 py-2.5 font-normal">
                    <span className="block text-[11px] font-semibold text-text-primary">{peer.facilityName}</span>
                    <span className="mt-0.5 block text-text-muted">{peer.locationLabel}</span>
                  </th>
                  <td className="px-2 py-2.5">
                    {water ? (
                      <>
                        <span className="block font-mono type-meta font-semibold uppercase tracking-[0.07em] text-primary">{water.stage}</span>
                        <span className="mt-1 block space-y-1 text-text-primary">
                          {water.values.map((value) => <span key={value} className="block">{value}</span>)}
                        </span>
                      </>
                    ) : <span className="text-text-muted">Not found in reviewed records</span>}
                  </td>
                  <td className="px-2 py-2.5 text-text-primary">
                    {cooling ?? <span className="text-text-muted">Not found in reviewed records</span>}
                  </td>
                  <td className="px-2 py-2.5 text-text-muted">{regionalRelevance(peer)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-[9px] leading-relaxed text-text-muted">
        A missing record means the reviewed public evidence did not establish the item. It is not evidence that a project used no water or had no cooling plant. Common approaches are only labelled as cohort patterns when at least three compatible projects support the same observation basis.
      </p>
    </section>
  );
};

const CoverageMarks: React.FC<{ peer: ComparableProjectPeerPresentation }> = ({ peer }) => (
  <span className="flex flex-wrap items-center gap-x-2 gap-y-1" aria-label="Record coverage">
    {COMPARABLE_PROJECT_FACETS.filter((facet) => facet !== 'overview').map((facet) => {
      const status = peer.coverage[facet];
      return (
        <span
          key={facet}
          className={cn(
            'inline-flex items-center gap-1 font-mono type-meta font-semibold tracking-[0.08em]',
            status === 'covered' ? 'text-success' : status === 'partial' ? 'text-warning' : 'text-text-muted',
          )}
          title={`${FACET_PRESENTATION[facet].label}: ${status}`}
        >
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full border',
              status === 'covered'
                ? 'border-success bg-success'
                : status === 'partial'
                  ? 'border-warning bg-warning/30'
                  : 'border-text-muted bg-transparent',
            )}
            aria-hidden
          />
          {FACET_PRESENTATION[facet].label}
        </span>
      );
    })}
  </span>
);

const DatumSources: React.FC<{ datum: ComparableProjectDatum }> = ({ datum }) => {
  if (!datum.sources?.length) return null;
  return (
    <ul className="mt-1 flex flex-wrap gap-x-2 gap-y-1" aria-label={`${datum.label} sources`}>
      {datum.sources.map((source, index) => (
        <li key={`${source.url ?? source.label}:${source.locator ?? index}`} className="min-w-0">
          {source.url ? (
            <a
              href={source.url}
              target="_blank"
              rel="noreferrer"
              title={source.excerpt}
              className="inline-flex max-w-full items-center gap-1 font-mono type-meta font-semibold tracking-[0.08em] text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <span className="truncate">{source.label}{source.locator ? ` · ${source.locator}` : ''}</span>
              <ExternalLink className="h-2.5 w-2.5 shrink-0" aria-hidden />
            </a>
          ) : (
            <p className="font-mono type-caption text-text-muted">{source.label}{source.locator ? ` · ${source.locator}` : ''}</p>
          )}
          {source.excerpt ? (
            <blockquote className="mt-1 max-w-[70ch] border-l border-border-subtle pl-2 text-[9px] leading-relaxed text-text-muted">
              “{source.excerpt}”
            </blockquote>
          ) : null}
        </li>
      ))}
    </ul>
  );
};

const DatumList: React.FC<{
  title: string;
  kind: 'fact' | 'inference';
  data: readonly ComparableProjectDatum[];
  emptyLabel?: string;
}> = ({ title, kind, data, emptyLabel = 'Not found in reviewed records' }) => (
  <section>
    <div className="flex items-center gap-2">
      {kind === 'fact'
        ? <BookOpenCheck className="h-3.5 w-3.5 text-primary" aria-hidden />
        : <Sparkles className="h-3.5 w-3.5 text-text-muted" aria-hidden />}
      <h5 className="font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-text-muted">{title}</h5>
    </div>
    <dl className="mt-2 divide-y divide-border-subtle border-y border-border-subtle">
      {data.length > 0 ? data.map((datum) => (
        <div key={datum.id} className="grid gap-1 py-2.5 sm:grid-cols-[minmax(9rem,0.7fr)_minmax(0,1.3fr)] sm:gap-4">
          <dt className="text-[11px] font-semibold leading-relaxed text-text-secondary">{datum.label}</dt>
          <dd className={cn('min-w-0 text-xs leading-relaxed', datum.kind === 'missing' ? 'text-text-muted' : 'text-text-primary')}>
            <div className="flex flex-wrap items-baseline gap-2">
              <span>{datum.value}</span>
              {datum.asOf ? <span className="font-mono type-meta tracking-[0.07em] text-text-muted">{formatDate(datum.asOf)}</span> : null}
              {datum.evidenceBasis ? (
                <span className="font-mono type-meta font-semibold tracking-[0.07em] text-primary">
                  {BASIS_LABEL[datum.evidenceBasis]}
                </span>
              ) : null}
            </div>
            <DatumSources datum={datum} />
          </dd>
        </div>
      )) : (
        <div className="py-3">
          <dt className="sr-only">Record coverage</dt>
          <dd className="text-xs text-text-muted">{emptyLabel}</dd>
        </div>
      )}
    </dl>
  </section>
);

const WaterLadder: React.FC<{ peer: ComparableProjectPeerPresentation }> = ({ peer }) => (
  <section data-testid="comparable-water-ladder">
    <div className="mb-3 flex items-center justify-between gap-3">
      <div>
        <h5 className="font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-text-primary">Water evidence over time</h5>
        <p className="mt-1 text-[11px] leading-relaxed text-text-muted">Proposal, legal authorization, and operating use remain separate.</p>
      </div>
      <Droplets className="h-4 w-4 shrink-0 text-primary" aria-hidden />
    </div>
    <ol className="grid gap-px border-y border-border-subtle bg-border-subtle md:grid-cols-2 xl:grid-cols-4" aria-label={`${peer.facilityName} water evidence timeline`}>
      {peer.waterTimeline.map((stage, index) => (
        <li key={stage.stage} className="relative bg-bg-base px-3 py-3">
          <div className="flex items-center gap-2">
            <span className={cn(
              'grid h-5 w-5 shrink-0 place-items-center rounded-full border font-mono type-meta font-semibold',
              stage.observations.some((item) => item.kind === 'fact')
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border-strong text-text-muted',
            )}>
              {index + 1}
            </span>
            <h6 className="text-[11px] font-semibold text-text-primary">{stage.label}</h6>
          </div>
          <div className="mt-2 space-y-2 pl-7">
            {stage.observations.map((datum) => (
              <div key={datum.id}>
                <p className={cn('text-[11px] leading-relaxed', datum.kind === 'missing' ? 'text-text-muted' : 'text-text-primary')}>{datum.value}</p>
                {datum.asOf ? <p className="mt-0.5 font-mono type-meta tracking-[0.07em] text-text-muted">{formatDate(datum.asOf)}</p> : null}
                <DatumSources datum={datum} />
              </div>
            ))}
          </div>
          {index < peer.waterTimeline.length - 1 ? <ArrowRight className="absolute -right-2.5 top-3.5 z-10 hidden h-4 w-4 bg-bg-base text-text-muted xl:block" aria-hidden /> : null}
        </li>
      ))}
    </ol>
  </section>
);

const DeliveryChains: React.FC<{ peer: ComparableProjectPeerPresentation }> = ({ peer }) => (
  <section data-testid="comparable-delivery-chains">
    <h5 className="font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-text-primary">Obstacle → response → outcome</h5>
    <p className="mt-1 text-[11px] leading-relaxed text-text-muted">The dated events are documented facts; their role in this sequence is a screening interpretation.</p>
    <div className="mt-3 space-y-3">
      {peer.deliveryChains.map((chain) => (
        <div key={chain.id} className="grid border-y border-border-subtle sm:grid-cols-3">
          {[
            ['Obstacle', chain.obstacle],
            ['Response / mitigation', chain.response],
            ['Outcome', chain.outcome],
          ].map(([label, datum], index) => {
            const item = datum as ComparableProjectDatum;
            return (
              <div key={String(label)} className="relative border-b border-border-subtle px-3 py-3 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
                <p className="font-mono type-meta font-semibold tracking-[0.09em] text-text-muted">{label as string}</p>
                <p className={cn('mt-1 text-[11px] leading-relaxed', item.kind === 'missing' ? 'text-text-muted' : 'text-text-primary')}>{item.value}</p>
                <DatumSources datum={item} />
                {index < 2 ? <ArrowRight className="absolute -right-2.5 top-3.5 z-10 hidden h-4 w-4 bg-bg-base text-text-muted sm:block" aria-hidden /> : null}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  </section>
);

const PeerFacetPanel: React.FC<{
  peer: ComparableProjectPeerPresentation;
  facet: ComparableProjectFacet;
}> = ({ peer, facet }) => {
  if (facet === 'water') {
    return (
      <div className="space-y-5">
        <WaterLadder peer={peer} />
        <DatumList title="Additional documented facts" kind="fact" data={peer.facts.water} />
        {peer.inferences.water.length > 0 ? <DatumList title="Screening interpretation" kind="inference" data={peer.inferences.water} /> : null}
      </div>
    );
  }
  if (facet === 'delivery') {
    return (
      <div className="space-y-5">
        <DeliveryChains peer={peer} />
        <DatumList title="Additional documented facts" kind="fact" data={peer.facts.delivery} />
        {peer.inferences.delivery.length > 0 ? <DatumList title="Screening interpretation" kind="inference" data={peer.inferences.delivery} /> : null}
      </div>
    );
  }
  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <DatumList title="Documented facts" kind="fact" data={peer.facts[facet]} />
      <DatumList title="Screening interpretation" kind="inference" data={peer.inferences[facet]} />
    </div>
  );
};

const ComparableFacetSourceAppendix: React.FC<{
  peers: readonly ComparableProjectPeerPresentation[];
}> = ({ peers }) => (
  <div className="mt-4 space-y-3 border-t border-border-subtle pt-3" data-testid="comparable-facet-source-appendix">
    <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.09em] text-text-muted">Facet source records</p>
    {peers.map((peer) => {
      const sources = peer.evidenceTrail.filter((step) => step.kind === 'official-record');
      if (sources.length === 0) return null;
      return (
        <section key={peer.projectId}>
          <h5 className="text-[11px] font-semibold text-text-primary">{peer.facilityName}</h5>
          <ul className="mt-1 grid gap-2 sm:grid-cols-2">
            {sources.map((source, index) => (
              <li key={`${source.sourceUrl ?? source.label}:${source.locator ?? index}`} className="min-w-0 border-l border-border-subtle pl-2 text-[10px] leading-relaxed text-text-secondary">
                <p className="font-semibold text-text-primary">{source.label}</p>
                <p>{source.detail}</p>
                {source.excerpt ? <blockquote className="mt-1 border-l border-border-subtle pl-2">“{source.excerpt}”</blockquote> : null}
                {source.locator ? <p className="mt-1 font-mono type-caption text-text-muted">{source.locator}</p> : null}
                {source.sourceUrl ? (
                  <a
                    href={source.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 break-all font-mono type-meta font-semibold tracking-[0.07em] text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    Open record <ExternalLink className="h-2.5 w-2.5 shrink-0" aria-hidden />
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      );
    })}
  </div>
);

export const ComparableProjectsAnswer: React.FC<{
  research: EconomicEvidenceResearchController;
  baselineSnapshot?: EconomicResearchResultSnapshot;
}> = ({ research, baselineSnapshot }) => {
  const liveSnapshot = research.run?.terminalResult === 'complete-with-evidence'
    && (research.run.resultSnapshot?.policyValidRecords.length ?? 0) > 0
    ? research.run.resultSnapshot
    : undefined;
  const displayedSnapshot = liveSnapshot ?? baselineSnapshot;
  const displayingBaseline = displayedSnapshot != null && liveSnapshot == null;
  // Methodology must describe the dossier that is actually on screen. Keep a
  // failed/empty refresh in the status component instead of attaching its
  // empty evidence trail to the reviewed-catalog fallback.
  const evidenceSnapshot = displayingBaseline
    ? displayedSnapshot
    : research.run?.resultSnapshot ?? displayedSnapshot;
  const presentation = useMemo(
    () => buildComparableProjectsPresentation(displayedSnapshot),
    [displayedSnapshot],
  );
  const [activePeerId, setActivePeerId] = useState<string | null>(presentation?.peers[0]?.projectId ?? null);
  const [activeFacet, setActiveFacet] = useState<ComparableProjectFacet>('overview');
  const activePeer = presentation?.peers.find((peer) => peer.projectId === activePeerId)
    ?? presentation?.peers[0]
    ?? null;

  useEffect(() => {
    if (!presentation?.peers.some((peer) => peer.projectId === activePeerId)) {
      setActivePeerId(presentation?.peers[0]?.projectId ?? null);
    }
  }, [activePeerId, presentation]);

  return (
    <div data-testid="comparable-projects-answer">
      <EconomicEvidenceResearchStatus
        research={research}
        baselineRecordCount={baselineSnapshot?.policyValidRecords.length ?? 0}
      />

      {presentation && activePeer ? (
        <div
          className="mt-5 border-t-2 border-text-primary pt-4"
          data-presentation-source={displayingBaseline ? 'reviewed-catalog' : 'live-research'}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.12em] text-primary">
                {displayingBaseline ? 'Reviewed catalog shortlist' : 'Comparable evidence shortlist'}
              </p>
              <p className="mt-1 text-sm font-semibold text-text-primary">
                {presentation.peerCount} policy-approved public-record {presentation.peerCount === 1 ? 'project' : 'projects'}
                <span className="font-normal text-text-muted"> · {presentation.basisSummary}</span>
              </p>
            </div>
            <p className="font-mono text-[9px] uppercase tracking-[0.08em] text-text-muted" data-testid="comparable-coverage-line">
              Coverage · {presentation.coverageLine}
            </p>
          </div>

          {presentation.operationalPeers.length > 0
            ? (
              <WaterCoolingComparison
                peers={presentation.operationalPeers}
                hasCompatibleCoolingPattern={presentation.aggregates.some((aggregate) => aggregate.topic === 'cooling')}
              />
            )
            : null}

          {presentation.aggregates.length > 0 ? (
            <section className="mt-4 border-y border-border-subtle py-3" data-testid="comparable-cohort-aggregates">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                <h4 className="text-xs font-semibold text-text-primary">Patterns across compatible projects</h4>
                <p className="font-mono type-meta tracking-[0.08em] text-text-muted">
                  Minimum 3 distinct projects per result
                </p>
              </div>
              <dl className="mt-2 grid gap-x-5 gap-y-3 sm:grid-cols-2">
                {presentation.aggregates.map((aggregate) => (
                  <div key={aggregate.id} className="min-w-0 border-l-2 border-primary/40 pl-2">
                    <dt className="font-mono type-meta font-semibold tracking-[0.07em] text-text-muted">
                      {aggregate.label} · n={aggregate.recordCount}
                    </dt>
                    <dd className="mt-0.5 text-[11px] leading-relaxed text-text-primary">{aggregate.value}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-2 text-[9px] leading-relaxed text-text-muted">
                Ranges retain their exact stage, flow, statistic, boundary, evidence basis, phase, units, time basis, and MW basis. They are not site forecasts.
              </p>
            </section>
          ) : null}

          <div
            className="mt-3 border-y border-border-subtle"
            role="tablist"
            aria-label="Comparable project shortlist"
            aria-orientation="vertical"
          >
            {presentation.peers.map((peer, index) => {
              const active = peer.projectId === activePeer.projectId;
              const capacity = conciseFact(peer, 'Documented capacity');
              const jobs = conciseFact(peer, 'Permanent onsite employment');
              return (
                <button
                  key={peer.projectId}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-controls={`comparable-peer-panel-${peer.projectId}`}
                  id={`comparable-peer-tab-${peer.projectId}`}
                  tabIndex={active ? 0 : -1}
                  onClick={() => {
                    setActivePeerId(peer.projectId);
                    setActiveFacet('overview');
                  }}
                  onKeyDown={(event) => {
                    const nextIndex = nextTabIndex(event.key, index, presentation.peers.length, 'vertical');
                    if (nextIndex === null) return;
                    const nextPeer = presentation.peers[nextIndex];
                    if (!nextPeer) return;
                    event.preventDefault();
                    setActivePeerId(nextPeer.projectId);
                    setActiveFacet('overview');
                    focusTab(`comparable-peer-tab-${nextPeer.projectId}`);
                  }}
                  className={cn(
                    'grid w-full min-w-0 gap-2 border-b border-border-subtle px-3 py-3 text-left outline-none transition-colors last:border-b-0 motion-reduce:transition-none sm:grid-cols-[2rem_minmax(9rem,1.1fr)_minmax(11rem,1fr)_minmax(8rem,0.9fr)] sm:items-center',
                    active ? 'bg-primary/5' : 'hover:bg-state-hover',
                    'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50',
                  )}
                  data-testid={`comparable-peer-${peer.projectId}`}
                >
                  <span className={cn('font-mono text-[10px] font-semibold tabular-nums', active ? 'text-primary' : 'text-text-muted')}>{String(index + 1).padStart(2, '0')}</span>
                  <span className="min-w-0">
                    <span className="block truncate text-xs font-semibold text-text-primary">{peer.facilityName}</span>
                    <span className="mt-0.5 block truncate text-[10px] text-text-muted">{peer.locationLabel}</span>
                  </span>
                  <span className="min-w-0 text-[10px] leading-relaxed text-text-secondary">
                    <span className="block">{[capacity, jobs].filter(Boolean).join(' · ') || 'Key project values not found'}</span>
                    <span className="mt-0.5 block truncate text-[9px] text-text-muted">
                      {peer.matchReasons[0] ?? 'Policy-valid project record'}
                    </span>
                  </span>
                  <span className="min-w-0">
                    <CoverageMarks peer={peer} />
                    <span className={cn('mt-1 block truncate text-[9px]', peer.outcome.kind === 'missing' ? 'text-text-muted' : 'text-text-secondary')}>
                      {peer.outcome.value}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <section
            id={`comparable-peer-panel-${activePeer.projectId}`}
            role="tabpanel"
            aria-labelledby={`comparable-peer-tab-${activePeer.projectId}`}
            className="mt-4"
            data-testid="comparable-peer-dossier"
          >
            <div className="flex flex-col gap-3 border-b border-border-subtle pb-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="font-sans text-lg font-semibold tracking-[-0.015em] text-text-primary">{activePeer.facilityName}</h4>
                </div>
                <p className="mt-1 max-w-[68ch] text-[11px] leading-relaxed text-text-secondary">
                  {activePeer.matchReasons[0] ?? 'Policy-valid project evidence matched to the current site.'}
                </p>
              </div>
              <p className="font-mono type-meta tracking-[0.08em] text-text-muted">
                {activePeer.reviewedSourceCount} reviewed {activePeer.reviewedSourceCount === 1 ? 'record' : 'records'}
                {activePeer.updatedAt ? ` · ${formatDate(activePeer.updatedAt)}` : ''}
              </p>
            </div>

            <div
              className="mt-3 flex max-w-full gap-1 overflow-x-auto pb-1"
              role="tablist"
              aria-label={`${activePeer.facilityName} dossier sections`}
              aria-orientation="horizontal"
            >
              {COMPARABLE_PROJECT_FACETS.map((facet, index) => {
                const item = FACET_PRESENTATION[facet];
                const Icon = item.icon;
                const active = activeFacet === facet;
                return (
                  <button
                    key={facet}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-controls={`comparable-facet-${activePeer.projectId}-${facet}`}
                    id={`comparable-facet-tab-${activePeer.projectId}-${facet}`}
                    tabIndex={active ? 0 : -1}
                    onClick={() => setActiveFacet(facet)}
                    onKeyDown={(event) => {
                      const nextIndex = nextTabIndex(event.key, index, COMPARABLE_PROJECT_FACETS.length, 'horizontal');
                      if (nextIndex === null) return;
                      const nextFacet = COMPARABLE_PROJECT_FACETS[nextIndex];
                      if (!nextFacet) return;
                      event.preventDefault();
                      setActiveFacet(nextFacet);
                      focusTab(`comparable-facet-tab-${activePeer.projectId}-${nextFacet}`);
                    }}
                    className={cn(
                      'inline-flex shrink-0 items-center gap-1.5 border-b-2 px-2.5 py-2 text-[10px] font-semibold outline-none transition-colors motion-reduce:transition-none',
                      active
                        ? 'border-primary text-text-primary'
                        : 'border-transparent text-text-muted hover:text-text-primary',
                      'focus-visible:ring-2 focus-visible:ring-primary/40',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" aria-hidden />
                    {item.label}
                    <span className="sr-only"> · {activePeer.coverage[facet]}</span>
                  </button>
                );
              })}
            </div>

            <div
              id={`comparable-facet-${activePeer.projectId}-${activeFacet}`}
              role="tabpanel"
              aria-labelledby={`comparable-facet-tab-${activePeer.projectId}-${activeFacet}`}
              className="mt-4"
              data-testid={`comparable-facet-${activeFacet}`}
            >
              {activeFacet === 'overview' ? (
                <div className="mb-5 grid gap-3 border-y border-border-subtle py-3 sm:grid-cols-[minmax(0,1fr)_minmax(12rem,0.55fr)]">
                  <div>
                    <p className="font-mono type-meta font-semibold tracking-[0.09em] text-text-muted">Record coverage</p>
                    <div className="mt-2"><CoverageMarks peer={activePeer} /></div>
                  </div>
                  <div className="sm:border-l sm:border-border-subtle sm:pl-4">
                    <p className="font-mono type-meta font-semibold tracking-[0.09em] text-text-muted">Outcome from reviewed chronology</p>
                    <p className={cn('mt-1 text-xs leading-relaxed', activePeer.outcome.kind === 'missing' ? 'text-text-muted' : 'text-text-primary')}>{activePeer.outcome.value}</p>
                    {activePeer.outcome.kind === 'inference' ? <p className="mt-1 font-mono type-meta font-semibold tracking-[0.07em] text-primary">Screening interpretation</p> : null}
                  </div>
                </div>
              ) : null}
              <PeerFacetPanel peer={activePeer} facet={activeFacet} />
            </div>
          </section>

          <p className="sr-only"><CheckCircle2 aria-hidden /> Comparable-project dossier available.</p>
        </div>
      ) : research.run?.terminalResult && research.run.terminalResult !== 'complete-with-evidence' ? (
        <div className="mt-4 flex items-center gap-2 border-y border-border-subtle py-3 text-xs text-text-muted">
          <CircleDashed className="h-4 w-4" aria-hidden />
          Comparable project details will appear when a policy-valid record is available.
        </div>
      ) : null}

      {evidenceSnapshot ? (
        <details className="mt-5 border-y border-border-subtle py-3" data-testid="comparable-methodology-evidence">
          <summary className="flex cursor-pointer list-none items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-text-primary">
            <Gauge className="h-3.5 w-3.5 text-primary" aria-hidden />
            Evidence trail
            <span className="font-normal text-text-muted">
              {evidenceSnapshot.policyValidRecords.length
                + evidenceSnapshot.held.length
                + evidenceSnapshot.excluded.length}
            </span>
          </summary>
          <div className="mt-3 border-l-2 border-primary/40 pl-3 text-[11px] leading-relaxed text-text-secondary">
            <p>Reviewed source records are attached to their exact claims. Water demand, authorization, and operating use remain separate; held and excluded records do not enter the peer count.</p>
          </div>
          {presentation ? <ComparableFacetSourceAppendix peers={presentation.peers} /> : null}
          <div className="mt-4"><EconomicEvidenceTrailSnapshotList snapshot={evidenceSnapshot} /></div>
        </details>
      ) : null}
    </div>
  );
};
